import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import dayjs from "dayjs";
import { useSetAtom } from "jotai";
import { Trans, useTranslation } from "react-i18next";

import {
  Flex,
  Title,
  Button,
  Divider,
  Checkbox,
  createStyles,
} from "@mantine/core";
import { useForm } from "@mantine/form";

import { currentStepAtom } from "../atoms/currentStep.atom";

import { Draft } from "@utils/Draft.util";
import { IIVolunteeringFormData } from "../VolunteeringFormRoute.types";
import { SubmissionConditionsInput } from "@components/SubmissionConditionsInput/SubmissionConditionsInput";
import { IStepRef } from "@models/Step.model";

const DATE_FORMAT = "dddd, MMMM DD, YYYY";
const TIME_FORMAT = "HH:mm";

const useStyles = createStyles((t) => ({
  form: {
    display: "flex",
    textAlign: "left",
    gap: t.spacing.md,
    flexDirection: "column",
  },
}));

export const Step2 = forwardRef<
  IStepRef<IIVolunteeringFormData>,
  {
    readonly?: boolean;
    isSubmitted?: boolean;
    isSavePending?: boolean;
    draft?: Draft<IIVolunteeringFormData>;
    onChange?: (change: Draft<IIVolunteeringFormData>) => void;
    onSubmit?: () => void;
  }
>(
  (
    {
      readonly = false,
      isSubmitted = false,
      isSavePending = false,
      draft = {},
      onChange = () => {},
      onSubmit = () => {},
    },
    ref
  ) => {
    const setCurrentStep = useSetAtom(currentStepAtom);
    const { classes } = useStyles();

    const { t } = useTranslation();

    const {
      demoTeam,
      demoAngel,
      backstageHelper,
      constructionHelper,
      meetingPointHelper,
      conditionsAccepted,
      prideBraceletDistributer,
    } = draft;

    const form = useForm({
      initialValues: {
        demoTeam,
        demoAngel,
        backstageHelper,
        constructionHelper,
        meetingPointHelper,
        conditionsAccepted,
        prideBraceletDistributer,
      },
    });

    const $toRefs = { onChange };
    const toRefs = useRef($toRefs);
    useEffect(() => {
      toRefs.current = $toRefs;
    });

    useEffect(() => {
      toRefs.current.onChange(form.values);
    }, [form.values]);

    useImperativeHandle(ref, () => ({
      getValues: () => form.values,
      isDirty: () => form.isDirty(),
      resetDirty: () => form.resetDirty(),
    }));

    return (
      <form className={classes.form} onSubmit={form.onSubmit(onSubmit)}>
        <Checkbox
          label={t("Demo Angel")}
          description={
            <Trans
              i18nKey={"forms.demo.labels.demoAngel.description"}
              // eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid
              components={{ br: <br /> }}
            ></Trans>
          }
          checked={form.values.demoAngel}
          {...form.getInputProps("demoAngel")}
        />
        <Checkbox
          label={t("Demo Team")}
          description={t(
            "I am interested in supporting the demo team in advance of the demo with a higher investment of time. I will be contacted by the demo manager about this"
          )}
          checked={form.values.demoTeam}
          {...form.getInputProps("demoTeam")}
        />
        <Divider />
        <Checkbox.Group
          orientation="vertical"
          label={<Title order={5}>{t("Construction helper:")}</Title>}
          {...form.getInputProps("constructionHelper")}
        >
          <Checkbox
            value="7/6/23 12:00-18:00"
            label={
              <>
                {dayjs("7/6/23").format(DATE_FORMAT)}{" "}
                {dayjs("7/6/23 12:00").format(TIME_FORMAT)} -{" "}
                {dayjs("7/6/23 18:00").format(TIME_FORMAT)}
              </>
            }
            description={t("The time will be coordinated individually.")}
          />
          <Checkbox
            value="7/7/23 10:00-14:00"
            label={
              <>
                {dayjs("7/7/23").format(DATE_FORMAT)}{" "}
                {dayjs("7/7/23 10:00").format(TIME_FORMAT)} -{" "}
                {dayjs("7/7/23 14:00").format(TIME_FORMAT)}
              </>
            }
          />
          <Checkbox
            value="7/9/23 22:00-00:00"
            label={
              <>
                {dayjs("7/9/23").format(DATE_FORMAT)}{" "}
                {dayjs("7/9/23 22:00").format(TIME_FORMAT)} -{" "}
                {dayjs("7/9/23 00:00").format(TIME_FORMAT)}
              </>
            }
          />
          <Checkbox
            value="7/10/23 11:00-15:00"
            label={
              <>
                {dayjs("7/10/23").format(DATE_FORMAT)}{" "}
                {dayjs("7/10/23 11:00").format(TIME_FORMAT)} -{" "}
                {dayjs("7/10/23 15:00").format(TIME_FORMAT)}
              </>
            }
          />
        </Checkbox.Group>
        <Divider />
        <Checkbox.Group
          orientation="vertical"
          label={<Title order={5}>{t("Backstage helper:")}</Title>}
          {...form.getInputProps("backstageHelper")}
        >
          <Checkbox
            value="7/7/23 15:00-23:30"
            label={
              <>
                {dayjs("7/7/23").format(DATE_FORMAT)}{" "}
                {dayjs("7/7/23 15:00").format(TIME_FORMAT)} -{" "}
                {dayjs("7/7/23 23:30").format(TIME_FORMAT)}
              </>
            }
            description={t("The time will be coordinated individually.")}
          />
          <Checkbox
            value="7/8/23 12:00-23:30"
            label={
              <>
                {dayjs("7/8/23").format(DATE_FORMAT)}{" "}
                {dayjs("7/8/23 12:00").format(TIME_FORMAT)} -{" "}
                {dayjs("7/8/23 23:30").format(TIME_FORMAT)}
              </>
            }
            description={t("The time will be coordinated individually.")}
          />
          <Checkbox
            value="7/9/23 11:00-22:30"
            label={
              <>
                {dayjs("7/9/23").format(DATE_FORMAT)}{" "}
                {dayjs("7/9/23 11:00").format(TIME_FORMAT)} -{" "}
                {dayjs("7/9/23 22:30").format(TIME_FORMAT)}
              </>
            }
            description={t("The time will be coordinated individually.")}
          />
        </Checkbox.Group>
        <Divider />
        <Checkbox.Group
          orientation="vertical"
          label={<Title order={5}>{t("Meeting point helper:")}</Title>}
          {...form.getInputProps("meetingPointHelper")}
        >
          <Checkbox
            value="7/7/23 15:00-23:30"
            label={
              <>
                {dayjs("7/7/23").format(DATE_FORMAT)}{" "}
                {dayjs("7/7/23 15:00").format(TIME_FORMAT)} -{" "}
                {dayjs("7/7/23 23:30").format(TIME_FORMAT)}
              </>
            }
            description={t("The time will be coordinated individually.")}
          />
          <Checkbox
            value="7/8/23 12:00-23:30"
            label={
              <>
                {dayjs("7/8/23").format(DATE_FORMAT)}{" "}
                {dayjs("7/8/23 12:00").format(TIME_FORMAT)} -{" "}
                {dayjs("7/8/23 23:30").format(TIME_FORMAT)}
              </>
            }
            description={t("The time will be coordinated individually.")}
          />
          <Checkbox
            value="7/9/23 11:00-22:30"
            label={
              <>
                {dayjs("7/9/23").format(DATE_FORMAT)}{" "}
                {dayjs("7/9/23 11:00").format(TIME_FORMAT)} -{" "}
                {dayjs("7/9/23 22:30").format(TIME_FORMAT)}
              </>
            }
            description={t("The time will be coordinated individually.")}
          />
        </Checkbox.Group>
        <Divider />
        <Checkbox.Group
          orientation="vertical"
          label={<Title order={5}>{t("Pride bracelet distributor:")}</Title>}
          {...form.getInputProps("prideBraceletDistributer")}
        >
          <Checkbox
            value="7/7/23 18:00-22:30"
            label={
              <>
                {dayjs("7/7/23").format(DATE_FORMAT)}{" "}
                {dayjs("7/7/23 18:00").format(TIME_FORMAT)} -{" "}
                {dayjs("7/7/23 22:30").format(TIME_FORMAT)}
              </>
            }
            description={t("The time will be coordinated individually.")}
          />
          <Checkbox
            value="7/8/23 15:00-23:00"
            label={
              <>
                {dayjs("7/8/23").format(DATE_FORMAT)}{" "}
                {dayjs("7/8/23 15:00").format(TIME_FORMAT)} -{" "}
                {dayjs("7/8/23 23:00").format(TIME_FORMAT)}
              </>
            }
            description={t("The time will be coordinated individually.")}
          />
          <Checkbox
            value="7/9/23 15:00-21:30"
            label={
              <>
                {dayjs("7/9/23").format(DATE_FORMAT)}{" "}
                {dayjs("7/9/23 15:00").format(TIME_FORMAT)} -{" "}
                {dayjs("7/9/23 21:30").format(TIME_FORMAT)}
              </>
            }
            description={t("The time will be coordinated individually.")}
          />
          <Checkbox
            value="7/9/23 10:00-15:00"
            label={
              <>
                Demo - {dayjs("7/9/23").format(DATE_FORMAT)}{" "}
                {dayjs("7/9/23 10:00").format(TIME_FORMAT)} -{" "}
                {dayjs("7/9/23 15:00").format(TIME_FORMAT)}
              </>
            }
            description={t("The time will be coordinated individually.")}
          />
        </Checkbox.Group>
        <Divider />
        <SubmissionConditionsInput
          showTermsAndConditions={false}
          conditionsAccepted={form.getInputProps("conditionsAccepted")}
        />
        {!readonly && (
          <Flex gap="md">
            <Button
              variant="outline"
              style={{ flex: 1 }}
              onClick={() => setCurrentStep((pv) => pv - 1)}
            >
              {t("Back")}
            </Button>
            <Button type="submit" style={{ flex: 1 }} loading={isSavePending}>
              {isSavePending
                ? `${t("Saving")}...`
                : t(
                    isSubmitted ? "forms.footer.update" : "forms.footer.submit"
                  )}
            </Button>
          </Flex>
        )}
      </form>
    );
  }
);
